/**
 * TrustedContactForm.js
 * Saisie du contact de confiance d'un bénéficiaire — PF-03 CP3.2 (D5, DEC-038).
 *
 * Présentationnel : prénom + email, validation locale identique au service
 * (beneficiaryService.setTrustedContact) et à send-email (F2a). Aucun <form>
 * rendu : le composant est aussi utilisé dans la modale Modifier, elle-même
 * un formulaire. Les boutons ne sont rendus que si leur libellé est fourni ;
 * onChange permet à un parent de lire la saisie sans bouton propre.
 */

import React, { useId, useState } from 'react';

const S = '#00F5A0';
const G = '#8892A4';

// Motif identique à beneficiaryService (EMAIL_PATTERN) et send-email (F2a).
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CONTACT_SEND_FAILED_MESSAGE =
  "Contact enregistré, mais l'email n'a pas pu être envoyé. Vous pourrez le renvoyer depuis la fiche du bénéficiaire.";

/** @returns {{ name: string, email: string }} prénom trimé, email trimé en minuscules. */
export const normalizeTrustedContact = (contact) => ({
  name: (contact?.name || '').trim(),
  email: (contact?.email || '').trim().toLowerCase(),
});

/** @returns {string|null} message d'erreur, ou null si le contact est valide. */
export const validateTrustedContact = (contact) => {
  const { name, email } = normalizeTrustedContact(contact);
  if (!name) return 'Le prénom du contact est obligatoire.';
  if (!EMAIL_PATTERN.test(email)) return 'Adresse email du contact invalide.';
  return null;
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 14,
  outline: 'none',
};

const labelStyle = { display: 'block', fontSize: 13, color: G, marginBottom: 6, fontWeight: 500 };

const TrustedContactForm = ({
  initial = null,
  submitLabel,
  secondaryLabel,
  onSubmit,
  onSecondary,
  onChange,
  busy = false,
  error = '',
}) => {
  const id = useId();
  const [name, setName] = useState(initial?.name || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [localError, setLocalError] = useState('');

  const handleNameChange = (value) => {
    setName(value);
    setLocalError('');
    onChange?.({ name: value, email });
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    setLocalError('');
    onChange?.({ name, email: value });
  };

  const handleSubmit = () => {
    const invalid = validateTrustedContact({ name, email });
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    onSubmit(normalizeTrustedContact({ name, email }));
  };

  const shownError = localError || error;

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <label htmlFor={`${id}-name`} style={labelStyle}>Prénom du contact</label>
        <input
          id={`${id}-name`}
          type="text"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          disabled={busy}
          autoComplete="off"
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label htmlFor={`${id}-email`} style={labelStyle}>Email du contact</label>
        <input
          id={`${id}-email`}
          type="email"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          disabled={busy}
          autoComplete="off"
          style={inputStyle}
        />
      </div>

      {shownError && (
        <p role="alert" style={{ margin: '0 0 12px', fontSize: 13, color: '#f87171' }}>{shownError}</p>
      )}

      {(submitLabel || secondaryLabel) && (
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          {submitLabel && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={busy}
              style={{ flex: 1, background: S, color: '#0A1628', border: 'none', borderRadius: 8, padding: '11px 0', fontWeight: 700, fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1 }}
            >
              {submitLabel}
            </button>
          )}
          {secondaryLabel && (
            <button
              type="button"
              onClick={onSecondary}
              disabled={busy}
              style={{ background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '11px 20px', fontWeight: 600, fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer' }}
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TrustedContactForm;
